/**
 * 작성자 : 홍선영
 * 날짜 : 2024.11.04
 * 기능 : (사내)설정관리-양식지관리-근태양식지 페이지
 */

import React, { useEffect, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useRecoilValue } from 'recoil';
import { themeState, userState } from '../../../atoms';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import ApplicationFormItems from './ApplicationFormItems';
import { dark, light } from '../../../assets/styles/theme';
import styled from 'styled-components';
import { useSetAuth } from '../../../utils/useSetAuth';
import { BtnBlue, BtnGreen } from '../../../components/Button';
import { useTranslation } from 'react-i18next';
import { apiPost } from '../../../services/_common';
import { toast } from 'react-toastify';
import { isMobile } from '../../../utils/checkMobile';
import IssueGuide from '../../../components/IssueGuide';
import { IApplicationFormType } from 'customTypes';
import { logPost } from '../../../services/log';
import { MOBILE_NOT_SUPPORT } from '../../../_constants';

const Root = styled.div`
  .closeIcon {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    padding: 0.5rem;
    font-size: 24px;
    color: var(--text-secondary);
    .material-symbols-rounded {
      font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
      font-size: 20px;
    }
    &:hover {
      color: var(--text-primary);
    }
  }

  .draggable-cell {
    &:hover {
      cursor: pointer !important;
    }
  }

  .subcd_18 > .editor-wrapper {
    flex-grow: 1;
    > div {
      .ck-editor__main > div {
        height: 38rem;
      }
    }
  }
`;

interface Cell {
  cdName: string;
  width: number;
  height: number;
  aRow: number | null;
  aCol: number | null;
  isDropped: boolean; // Drop 여부
  subCd: string;
  aCd: string;
  aContents: string;
}

const DragZone: React.FC<{ cells: Cell[]; setCells: React.Dispatch<React.SetStateAction<Cell[]>>; logo: string; formType: IApplicationFormType }> = ({ cells, setCells, logo, formType }) => {
  return (
    <div style={{ width: '50%', padding: '10px', overflowY: 'scroll' }}>
      {cells
        .filter((cell) => !cell.isDropped)
        .map((cell) => (
          <DraggableCell key={cell.cdName} cell={cell} setCells={setCells} logo={logo} formType={formType} />
        ))}
    </div>
  );
};

interface DropZoneProps {
  cells: Cell[];
  onDrop: (item: Cell, target: { aRow: number; aCol: number }) => void;
  setCells: React.Dispatch<React.SetStateAction<Cell[]>>;
  logo: string;
  formType: 'attendance' | 'creport' | 'cdraft';
}

const GRID_COL_COUNT = 3;
const GRID_ROW_COUNT = 31;
const CELL_HEIGHT = 50;

const getGridLocation = (offset: { x: number; y: number }, dropZoneRect: DOMRect, scrollOffset: { scrollTop: number; scrollLeft: number }) => {
  const clientHeight = GRID_ROW_COUNT * CELL_HEIGHT;
  const gridHeight = clientHeight / GRID_ROW_COUNT;
  const clientWidth = dropZoneRect.width;
  const gridWidth = clientWidth / GRID_COL_COUNT;

  const gridX = Math.floor((offset.x - dropZoneRect.left + scrollOffset.scrollLeft) / gridWidth);
  const gridY = Math.floor((offset.y - dropZoneRect.top + scrollOffset.scrollTop) / gridHeight);

  return { gridX, gridY };
};

const DropZone: React.FC<DropZoneProps> = ({ cells, onDrop, setCells, logo, formType }) => {
  const dropZoneRef = React.useRef<HTMLDivElement | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ aRow: number; aCol: number } | null>(null);

  const [, drop] = useDrop(() => ({
    accept: 'CELL',
    hover: (item: Cell, monitor) => {
      const dropZoneRect = dropZoneRef.current?.getBoundingClientRect();
      const offset = monitor.getClientOffset();
      const scrollOffset = {
        scrollTop: dropZoneRef.current?.scrollTop || 0,
        scrollLeft: dropZoneRef.current?.scrollLeft || 0,
      };

      if (offset && dropZoneRect) {
        const { gridX, gridY } = getGridLocation(offset, dropZoneRect, scrollOffset);
        setHoveredCell({ aRow: gridY, aCol: gridX }); // hover 상태 업데이트
      }
    },
    drop: (item: Cell, monitor) => {
      const dropZoneRect = dropZoneRef.current?.getBoundingClientRect();
      const offset = monitor.getClientOffset();
      const scrollOffset = {
        scrollTop: dropZoneRef.current?.scrollTop || 0,
        scrollLeft: dropZoneRef.current?.scrollLeft || 0,
      };

      if (offset && dropZoneRect) {
        const { gridX, gridY } = getGridLocation(offset, dropZoneRect, scrollOffset);
        onDrop(item, { aRow: gridY, aCol: gridX });
      }
      setHoveredCell(null); // 드롭 후 hover 상태 초기화
    },
    leave: () => {
      setHoveredCell(null); // hover가 끝나면 초기화
    },
  }));

  return (
    <div
      ref={(node) => {
        drop(node);
        dropZoneRef.current = node;
      }}
      style={{
        width: '50%',
        display: 'grid',
        gridTemplateColumns: `repeat(${GRID_COL_COUNT}, 1fr)`,
        gridTemplateRows: `repeat(${GRID_ROW_COUNT}, ${CELL_HEIGHT}px)`,
        padding: '10px',
        overflowY: 'auto',
      }}
    >
      {Array.from({ length: GRID_COL_COUNT * GRID_ROW_COUNT }).map((_, index) => {
        const row = Math.floor(index / GRID_COL_COUNT); // 열 개수를 기준으로 계산
        const col = index % GRID_COL_COUNT; // 열 인덱스 계산
        const cell = cells.find((c) => c.aRow === row && c.aCol === col);
        const isHovered = hoveredCell?.aRow === row && hoveredCell?.aCol === col;

        return (
          <div
            key={index}
            style={{
              width: '100%',
              height: `${CELL_HEIGHT}px`,
              backgroundColor: isHovered ? '#d6eeee' : 'transparent', // hover 배경색 추가
              position: 'relative',
            }}
          >
            {cell && <DraggableCell cell={cell} setCells={setCells} logo={logo} formType={formType} />}
          </div>
        );
      })}
    </div>
  );
};

const DraggableCell: React.FC<{ cell: Cell; setCells: React.Dispatch<React.SetStateAction<Cell[]>>; logo: string; formType: IApplicationFormType }> = ({ cell, setCells, logo, formType }) => {
  const [, drag] = useDrag(() => ({
    type: 'CELL',
    item: cell,
  }));
  const theme = useRecoilValue(themeState);

  const onClickRemove = () => {
    setCells((prev) =>
      prev.map((c) =>
        c.cdName === cell.cdName
          ? { ...c, aRow: null, aCol: null, isDropped: false } // 셀을 초기화하여 좌측으로 이동
          : c
      )
    );
  };

  return (
    <div
      className='draggable-cell'
      ref={drag}
      style={{
        position: 'relative',
        width: cell.isDropped ? `${cell.width * 100}%` : `calc((100% / ${GRID_COL_COUNT}) * ${cell.width})`, // 동적으로 width 계산
        // height: `calc(${CELL_HEIGHT}px * ${cell.height})`, // height는 고정된 그리드 높이에 비례
        height: `${CELL_HEIGHT * cell.height}px`, // height는 고정된 그리드 높이에 비례
        backgroundColor: theme === 'light' ? light.tonal : dark.tonal,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontWeight: 'bold',
        cursor: 'move',
        zIndex: 1,
        margin: cell.isDropped ? '' : '5px',
      }}
    >
      {ApplicationFormItems({ type: 'init_form', data: null, formType }, cell.subCd, cell.cdName, cell, setCells, logo)}
      {cell.isDropped && (
        <div className='closeIcon' onClick={onClickRemove} role='presentation'>
          <span className='material-symbols-rounded'>cancel </span>
        </div>
      )}
    </div>
  );
};

const CustomForm = ({ formType }: { formType: IApplicationFormType }) => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue(userState);
  const [cells, setCells] = useState<Cell[]>([]);
  const [logo, setLogo] = useState<string>('');
  const [orgCells, setOrgCells] = useState<Cell[]>([]);
  const { data: formSet } = GetFetchStatus({ path: `/${formType}/set`, req: { cCd: userInfo.hCd }, useLoginUserInfo: false });
  const { data: cInfo } = GetFetchStatus({ path: '/company/info/one', req: { cCd: userInfo.hCd }, useLoginUserInfo: false });

  useEffect(() => {
    if (cInfo) setLogo(cInfo[0]?.cLogoimage);
  }, [cInfo]);

  useEffect(() => {
    if (formSet) {
      const set = formSet.map((el: any) => ({ ...el, isDropped: el.aCol !== null && el.aRow !== null, height: Number(el.height), width: Number(el.width) }));
      setCells(set);
      setOrgCells(set);
    }
  }, [formSet, formType]);

  const handleDrop = (item: Cell, target: { aRow: number; aCol: number }) => {
    setCells((prev) => {
      // 셀의 높이와 너비를 고려하여 드롭 가능한지 확인
      const isPositionOccupied = prev.some((cell) => {
        if (cell.isDropped && cell.subCd !== item.subCd) {
          const isOverlapping =
            target.aRow < (cell.aRow ?? 0) + cell.height && target.aRow + item.height > (cell.aRow ?? 0) && target.aCol < (cell.aCol ?? 0) + cell.width && target.aCol + item.width > (cell.aCol ?? 0);
          return isOverlapping;
        }
        return false;
      });

      const isOutOfBounds = target.aRow < 0 || target.aCol < 0 || target.aRow + item.height > GRID_ROW_COUNT || target.aCol + item.width > GRID_COL_COUNT;

      if (isPositionOccupied) {
        console.log('Position is occupied. Dropping is not allowed.');
        return prev; // 위치가 겹친다면 변경하지 않음
      }

      if (isOutOfBounds) {
        console.log('Target position is out of bounds.');
        return prev; // 그리드를 초과하는 경우 무시
      }

      // 셀 위치 업데이트
      return prev.map((cell) => (cell.subCd === item.subCd ? { ...cell, aCd: cell.subCd, aRow: target.aRow, aCol: target.aCol, isDropped: true } : cell));
    });
  };

  const onClickSave = async () => {
    try {
      const result = cells.filter((el: Cell) => el.isDropped).map(({ aCd, aRow, aCol, aContents }) => ({ cCd: userInfo.hCd, aCd, aRow, aCol, aContents }));
      const req = { inputSets: result };
      const res = await apiPost({ path: `/${formType}/set`, req });
      if (res.data.statusCode === 200) {
        setOrgCells(cells);
        toast.success(t(res.data.message));
        const formName = formType === 'attendance' ? '근태 양식' : formType === 'cdraft' ? '기안 양식' : '보고서 양식';
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '환경 설정 > 양식 설정',
          action: '저장',
          etc: `${formName} 설정 저장`,
        });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  if (isMobile()) {
    return (
      <Root>
        <IssueGuide text={MOBILE_NOT_SUPPORT} type='mobile' />
      </Root>
    );
  }

  return (
    <div>
      <DndProvider backend={HTML5Backend}>
        <Root style={{ display: 'flex', gap: '20px', overflowY: 'auto' }}>
          <DragZone cells={cells} setCells={setCells} logo={logo} formType={formType} />
          <DropZone cells={cells} setCells={setCells} onDrop={handleDrop} logo={logo} formType={formType} />
        </Root>
      </DndProvider>
      {(auth.createAuth || auth.updateAuth) && (
        <ButtonsWrapper>
          <BtnGreen type='button' onClick={() => setCells(orgCells)}>
            {t('초기화')}
          </BtnGreen>
          <BtnBlue type='button' onClick={onClickSave}>
            {t('저장')}
          </BtnBlue>
        </ButtonsWrapper>
      )}
    </div>
  );
};

export default CustomForm;

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  justify-content: flex-end;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;
